import moment from 'moment';

export type TimelineActivitySession = {
  in: string;
  out: string;
  usage?: string;
  idle?: string;
  appName?: string;
  windowName?: string;
  [key: string]: unknown;
};

export type TimelineRenderedSession = TimelineActivitySession & {
  data: number;
  date: string;
  in: string;
  out: string;
  label: 'idle' | 'usage';
  inTime: string;
  outTime: string;
};

function formateTimeInDecimal(formattedEarliestTime: string): number {
  const timeParts = formattedEarliestTime.split(':');
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);
  return Math.round((hours + minutes / 60) * 100) / 100;
}

function getNewTimeAfterDifference(inTime: string, outTime: string): string {
  const inTimestamp = new Date(`1970-01-01T${inTime}Z`).getTime();
  const outTimestamp = new Date(`1970-01-01T${outTime}Z`).getTime();
  const timeDifference = outTimestamp - inTimestamp;
  const hours = Math.floor(timeDifference / 3600000);
  const paddedHours = String(hours).length > 1 ? String(hours) : String(hours).padStart(2, '0');
  const minutes = Math.floor((timeDifference % 3600000) / 60000);
  const paddedMinutes = String(minutes).length > 1 ? String(minutes) : String(minutes).padStart(2, '0');
  const seconds = Math.floor((timeDifference % 60000) / 1000);
  const paddedSeconds = String(seconds).length > 1 ? String(seconds) : String(seconds).padStart(2, '0');
  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
}

function getTimeDifference(inTime: string, outTime: string): number {
  const inTimestamp = new Date(`1970-01-01T${inTime}Z`).getTime();
  const outTimestamp = new Date(`1970-01-01T${outTime}Z`).getTime();
  const timeDifference = outTimestamp - inTimestamp;
  const hours = Math.floor(timeDifference / 3600000);
  const minutes = Math.floor((timeDifference % 3600000) / 60000);
  const seconds = Math.floor((timeDifference % 60000) / 1000);
  return formateTimeInDecimal(
    `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  );
}

function combineDateAndTime(date: string, hhmmss: string): string {
  return `${date} ${hhmmss}`;
}

/**
 * Turns raw application-log sessions into floating-bar segments for the
 * dashboard / employee-profile timeline.
 *
 * Mixed usage+idle rows are split so idle at the end of the interval is drawn.
 * (SUSEE-1498: a 15:11–18:30 row with 1m usage + 3h idle was previously dropped.)
 */
export function processTimelineSessionLogs(
  sessions: TimelineActivitySession[] | null | undefined
): TimelineRenderedSession[] {
  if (!sessions || sessions.length === 0) {
    return [];
  }

  const sessionLogs: TimelineRenderedSession[] = [];
  sessions.forEach((item, it) => {
    const usage = item.usage || '00:00:00';
    const idle = item.idle || '00:00:00';
    const idlebool = idle !== '00:00:00';
    const usagebool = usage !== '00:00:00';
    const data = formateTimeInDecimal(usagebool ? usage : idle);
    const itemout =
      moment(item.in).format('YYYY-MM-DD') === moment(item.out).format('YYYY-MM-DD')
        ? moment(item.out).format('HH:mm:ss')
        : '23:59:59';
    const itemin = moment(item.in).format('HH:mm:ss');
    const date = moment(item.in).format('YYYY-MM-DD');

    if (it === 0) {
      sessionLogs.push({
        ...item,
        data,
        date,
        in: itemin,
        out: itemout,
        label: idlebool ? 'idle' : 'usage',
        inTime: item.in,
        outTime: item.out,
      });
      return;
    }

    const prevSessionLog = sessionLogs[sessionLogs.length - 1];

    if (!usagebool && idlebool && prevSessionLog.label === 'idle' && prevSessionLog.usage === '00:00:00') {
      const difference = getTimeDifference(prevSessionLog.out, itemin);
      if (difference > 0.02) {
        sessionLogs.push({
          ...item,
          data,
          date,
          in: itemin,
          out: itemout,
          label: 'idle',
          inTime: item.in,
          outTime: item.out,
        });
      } else {
        prevSessionLog.out = itemout;
        prevSessionLog.data += data;
        prevSessionLog.appName = item.appName;
        prevSessionLog.windowName = item.windowName;
        prevSessionLog.outTime = item.out;
      }
    } else if (!usagebool && idlebool && prevSessionLog.label === 'usage') {
      sessionLogs.push({
        ...item,
        data,
        date,
        in: itemin,
        out: itemout,
        label: 'idle',
        inTime: item.in,
        outTime: item.out,
      });
    } else if (usagebool && !idlebool && prevSessionLog.label === 'usage' && prevSessionLog.idle === '00:00:00') {
      const difference = getTimeDifference(prevSessionLog.out, itemin);
      if (difference > 0.02) {
        sessionLogs.push({
          ...item,
          data,
          date,
          in: itemin,
          out: itemout,
          label: 'usage',
          inTime: item.in,
          outTime: item.out,
        });
      } else {
        prevSessionLog.out = itemout;
        prevSessionLog.data += data;
        prevSessionLog.appName = item.appName;
        prevSessionLog.windowName = item.windowName;
        prevSessionLog.outTime = item.out;
      }
    } else if (usagebool && !idlebool && prevSessionLog.label === 'idle') {
      sessionLogs.push({
        ...item,
        data,
        date,
        in: itemin,
        out: itemout,
        label: 'usage',
        inTime: item.in,
        outTime: item.out,
      });
    } else if (usagebool && idlebool && prevSessionLog.label === 'usage') {
      const idleStart = getNewTimeAfterDifference(idle, itemout);
      prevSessionLog.out = idleStart;
      prevSessionLog.data += data;
      prevSessionLog.appName = item.appName;
      prevSessionLog.windowName = item.windowName;
      prevSessionLog.outTime = combineDateAndTime(date, idleStart);
      sessionLogs.push({
        ...item,
        data: formateTimeInDecimal(idle),
        date,
        in: idleStart,
        out: itemout,
        label: 'idle',
        inTime: combineDateAndTime(date, idleStart),
        outTime: item.out,
      });
    } else if (usagebool && idlebool && prevSessionLog.label === 'idle') {
      const newOutInTime = getNewTimeAfterDifference(usage, itemout);
      prevSessionLog.out = newOutInTime;
      prevSessionLog.data += data;
      prevSessionLog.appName = item.appName;
      prevSessionLog.windowName = item.windowName;
      prevSessionLog.outTime = item.out;
      sessionLogs.push({
        ...item,
        data,
        appName: item.appName,
        windowName: item.windowName,
        date,
        in: newOutInTime,
        out: moment(item.out).format('HH:mm:ss'),
        label: 'usage',
        inTime: item.in,
        outTime: item.out,
      });
    }
  });

  return sessionLogs;
}
